import {
  commerceApi,
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { EGerdauService } from '../EGerdauService';
import {
  IDataResponseOrderSimulate,
  IDataOrderSimulate,
  ICheckoutResponse,
  IPayloadOrderSimulate,
  ISalesParams,
  IOrderSimulateState,
  ITaxesCalculatePayload,
  ITaxesCalculateResponse,
  IEstimatedDatePayload,
  IEstimatedDateResponse
} from '../../store/ducks/order-simulate-checkout-mvp/types';
import { IItemSaleInfo } from '../../store/ducks/long-steel-shopping-cart/types';
import { ELocalStorageKey, ILocalStorageObj } from '../EGerdauService/types';
import { CHECKOUT_ORDERS_INITIAL_STATE } from '../../store/ducks/order-simulate-checkout-mvp';

export class CheckoutServiceMvp extends EGerdauService {
  private localStorageKey = ELocalStorageKey.CHECKOUT;
  constructor() {
    super();
  }

  /**
   * Gera simulação de ordem
   * @param bodyRequest request object data
   * @returns res
   */
  async generateOrderSimulate(params: IDataOrderSimulate) {
    // const dispatch = useDispatch();
    const headers = new EGerdauService().getHeaders();
    const requestURL = ECustomerAuthorizationEndpoints.ORDER_SIMULATE;
    const payload: IPayloadOrderSimulate = this.setPayloadOrderSimulate(params);

    try {
      const response = await customersAuthorizationApi.post(
        requestURL,
        payload,
        {
          headers
        }
      );
      const typedResponse = response.data as IDataResponseOrderSimulate;

      const result: ICheckoutResponse = {
        orders: typedResponse.orders,
        o_price_sap_totals: typedResponse.o_price_sap_totals,
        cartId: params.cartNumber,
        cartCreatedIn: params.createdAt,
        cartUpdatedIn: params.updatedAt
      };
      return result;
    } catch (error) {
      return new Error('Serviço indisponível temporariamente');
    }
  }

  /**
   * Calculate taxes for checkout items
   * @param payload request object data for tax calculation
   * @returns ITaxesCalculateResponse with tax breakdown
   */
  async calculateTaxes(
    payload: ITaxesCalculatePayload
  ): Promise<ITaxesCalculateResponse | Error> {
    const eGerdauService = new EGerdauService();
    const headers = {
      ...eGerdauService.getHeaders({ withCommerceToken: true }),
      client_id: process.env.REACT_APP_token_client_id as string,
      client_secret: process.env.REACT_APP_token_client_secret as string
    };
    const requestURL = ECustomerAuthorizationEndpoints.TAXES_CALCULATE;

    try {
      const response = await customersAuthorizationApi.post(
        requestURL,
        payload,
        {
          headers
        }
      );
      return response.data as ITaxesCalculateResponse;
    } catch (error) {
      console.error('Error calculating taxes:', error);
      return new Error('Erro ao calcular impostos');
    }
  }

  /**
   * Get estimated delivery dates for checkout items
   * @param payload request object data for estimated date calculation
   * @returns IEstimatedDateResponse with delivery date information
   */
  async getEstimatedDate(
    payload: IEstimatedDatePayload
  ): Promise<IEstimatedDateResponse | Error> {
    const eGerdauService = new EGerdauService();
    const headers = {
      ...eGerdauService.getHeaders({ withCommerceToken: true }),
      client_id: process.env.REACT_APP_token_client_id as string,
      client_secret: process.env.REACT_APP_token_client_secret as string
    };
    const requestURL = ECustomerAuthorizationEndpoints.ESTIMATED_DATE;

    try {
      const response = await customersAuthorizationApi.post(
        requestURL,
        payload,
        {
          headers
        }
      );
      return response.data as IEstimatedDateResponse;
    } catch (error) {
      console.error('Error getting estimated date:', error);
      return new Error('Erro ao buscar data estimada');
    }
  }

  /**
   * Get cart data
   * @param cartId cart identifier
   * @returns cart data
   */
  async deleteAllCartItems(cartId: string) {
    const headers = new EGerdauService().getHeaders();

    const requestURL = `${ECustomerAuthorizationEndpoints.CARTS}/${cartId}/items`;
    const response = await commerceApi.delete(requestURL, {
      headers
    });
    return response;
  }

  async sendOrder(salesParams: ISalesParams): Promise<string[]> {
    try {
      const sendCommerToken = salesParams.isNewCheckoutFlow ?? false;
      const headers = super.getHeaders({ withCommerceToken: sendCommerToken });

      const itemsSaleInfo: IItemSaleInfo[] = salesParams.data.map((item) => {
        delete item.minimumQuantity;
        delete item.multipleQuantity;
        return item;
      });

      if (salesParams.isNewCheckoutFlow) {
        const response = await customersAuthorizationApi.post(
          `${ECustomerAuthorizationEndpoints.NEGOTIABLE_QUOTE}`,
          {
            items: itemsSaleInfo,
            soldTo: salesParams.emitterId,
            shipTo: salesParams.receiverId,
            purchaseNumber: salesParams.customerOrder,
            cartNumber: salesParams.cartNumber,
            quoteName: !salesParams.customerOrder
              ? 'n/a'
              : salesParams.customerOrder,
            ...(salesParams.taxes && { taxes: salesParams.taxes })
          },
          {
            headers,
            params: {
              soldTo: salesParams.emitterId,
              shipTo: salesParams.receiverId
            }
          }
        );
        if (typeof response.data === 'string') {
          throw new Error(response.data);
        }
      } else {
        // Legacy endpoint - entry-order-v2

        const response = await customersAuthorizationApi.post(
          `${ECustomerAuthorizationEndpoints.SEND_ORDER_V2}?customer=${salesParams.emitterId}`,
          {
            items: itemsSaleInfo,
            soldTo: salesParams.emitterId,
            shipTo: salesParams.receiverId,
            purchaseNumber: salesParams.customerOrder,
            cartNumber: salesParams.cartNumber
          },
          { headers }
        );
        if (typeof response.data === 'string') {
          throw new Error(response.data);
        }
      }

      const createdOrders: string[] = [salesParams.customerOrder ?? ''];

      return createdOrders;
    } catch (error) {
      throw new Error(
        'Seu pedido não pôde ser enviado. Entre em contato com o seu vendedor.'
      );
    }
  }

  /**
   * Get cart items data
   * @param cartId cart identifier
   * @returns cart items data
   */
  async getItemsFromCart(cartId: string) {
    const headers = new EGerdauService().getHeaders();

    const requestURL = `${ECustomerAuthorizationEndpoints.CARTS}/${cartId}/items`;
    const response = await commerceApi.get(requestURL, {
      headers
    });
    return response.data;
  }

  updateCheckoutStateLocalStorage(checkoutState: IOrderSimulateState): void {
    super.setLocalStorage(this.localStorageKey, checkoutState);
  }

  getCheckoutFromLocalStorage(): IOrderSimulateState {
    const localStorageData = super.getLocalStorage(this.localStorageKey);

    return localStorageData && this.canReturnLocalStorageData(localStorageData)
      ? localStorageData.obj
      : CHECKOUT_ORDERS_INITIAL_STATE;
  }

  private canReturnLocalStorageData(
    localStorageData: ILocalStorageObj
  ): boolean {
    const now = new Date();
    return now.getTime() - localStorageData.dateTime <= 3600000; // 1h
  }

  private setPayloadOrderSimulate(
    params: IDataOrderSimulate
  ): IPayloadOrderSimulate {
    return {
      cartNumber: params.cartNumber,
      purchaseNumber: params.purchaseNumber,
      shipTo: params.shipTo,
      soldTo: params.soldTo,
      items: params.items.map((item) => ({
        activitySector: item.activitySector,
        benefit: item.benefit,
        consumptionOrder: item.consumptionOrder,
        distrChannel: item.distrChannel,
        industrialType: item.industrialType,
        item_id_gerdau: item.item_id_gerdau,
        material: item.material,
        plant: item.plant,
        price_agreement_date_gerdau: item.price_agreement_date_gerdau,
        quantity: item.quantity,
        requiredDate: item.requiredDate,
        salesGroup: item.salesGroup,
        salesOffice: item.salesOffice,
        salesOrg: item.salesOrg,
        term: item.term,
        unityMeasure: item.unityMeasure
      }))
    };
  }
}
